import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import api from 'services/api';

/**
 * O backend resolve a plataforma pelo nome do tipo de integração ('SYSCALL'),
 * enquanto as telas comparam com o rótulo do cadastro antigo ('Syscall').
 */
const GATEWAY_POR_PLATAFORMA = {
  SYSCALL: 'Syscall',
  VONIX: 'Vonix',
  JDN: 'JDN',
  OLOS: 'Olos',
  OUTRO: 'Outro',
  EXPERTVOICE: 'ExpertVoice',
  'EXPERT-VOICE': 'ExpertVoice',
  '3CPLUS': '3CPlus',
  '3CX': '3CX',
};

function gatewayDaPlataforma(plataforma) {
  return GATEWAY_POR_PLATAFORMA[String(plataforma || '').toUpperCase()] || '';
}

/**
 * Sessão de discador do colaborador logado.
 *
 * No novo padrão a plataforma, o agente e o ramal vêm do /discador_sessao; o
 * cadastro (`disc_gateway`, `ramal`, `ramal2`) é o fallback enquanto a empresa
 * não migrar, e quem aplica esse fallback é o próprio endpoint — as telas não
 * precisam saber qual plataforma está migrada.
 *
 * Os `obter*` leem de ref porque são chamados dentro de handlers de WebSocket,
 * que podem estar com closure de um render anterior.
 */
export default function useSessaoDiscador() {
  const authUserInfo = useSelector(({ auth }) => auth.authUserInfo);

  const [sessao, setSessao] = useState(null);
  const [carregada, setCarregada] = useState(false);

  const sessaoRef = useRef(null);

  useEffect(() => {
    sessaoRef.current = sessao;
  }, [sessao]);

  useEffect(() => {
    let montado = true;

    (async () => {
      try {
        const { data } = await api.get('/discador_sessao');
        if (montado) setSessao(data);
      } catch (error) {
        // sem sessão resolvida as telas seguem com os dados do cadastro
      } finally {
        if (montado) setCarregada(true);
      }
    })();

    return () => {
      montado = false;
    };
  }, []);

  /**
   * Respondida a sessão, ela é a verdade — sem isso uma escolha pendente cairia
   * no `disc_gateway` do cadastro e o painel ativaria uma plataforma que o
   * operador não escolheu. Sem sessão (em voo ou falhou), o cadastro segura.
   */
  const plataforma = sessao
    ? gatewayDaPlataforma(sessao.plataforma) || sessao.plataforma || ''
    : authUserInfo?.disc_gateway || '';

  const plataformasDisponiveis = (sessao?.plataformas_disponiveis || []).map(
    (item) => ({
      ...item,
      gateway: gatewayDaPlataforma(item.plataforma) || item.plataforma,
    })
  );

  /** Grava a escolha e já recebe a sessão nova de volta. */
  async function selecionarPlataforma(plataformaEscolhida) {
    const { data } = await api.post(
      `/discador_selecionar_plataforma?plataforma=${plataformaEscolhida}`
    );

    setSessao(data);

    return data;
  }

  return {
    sessao,
    carregada,
    plataforma,
    plataformasDisponiveis,
    selecaoPendente: !!sessao?.selecao_pendente,
    selecionarPlataforma,
    agente: sessao ? sessao.agente || '' : authUserInfo?.ramal || '',
    ramal: sessao ? sessao.ramal || '' : authUserInfo?.ramal2 || '',
    obterAgente: () =>
      sessaoRef.current
        ? sessaoRef.current.agente || ''
        : authUserInfo?.ramal || '',
    obterRamal: () =>
      sessaoRef.current
        ? sessaoRef.current.ramal || ''
        : authUserInfo?.ramal2 || '',
    obterConexao: () => sessaoRef.current?.conexao || null,
  };
}
